import { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  zarzadyZlewni,
  type ZarzadZlewniDto,
  type ZarzadStudyRef,
} from '@/api/endpoints';
import { Icon } from '@/components/Icon';

export function ZarzadZlewniDetail() {
  const { id } = useParams<{ id: string }>();
  const zid = Number(id);

  const [zz, setZz] = useState<ZarzadZlewniDto | null>(null);
  const [studiesList, setStudiesList] = useState<ZarzadStudyRef[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Notes editing
  const [notes, setNotes] = useState<string>('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSavedAt, setNotesSavedAt] = useState<number | null>(null);

  // Custom AI prompt editing — doklejany do AiService.budujPrompt dla opracowań pod tym ZZ.
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [promptDirty, setPromptDirty] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptSavedAt, setPromptSavedAt] = useState<number | null>(null);

  // Filter for studies table
  const [filter, setFilter] = useState<string>('');
  const [onlyRejected, setOnlyRejected] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(zid)) return;
    zarzadyZlewni.detail(zid)
      .then(d => { setZz(d); setNotes(d.notes ?? ''); setCustomPrompt(d.customAiPrompt ?? ''); })
      .catch(e => setErr(String(e)));
    zarzadyZlewni.studies(zid)
      .then(setStudiesList)
      .catch(() => setStudiesList([]));
  }, [zid]);

  const filteredStudies = useMemo(() => {
    if (!studiesList) return [];
    let arr = studiesList;
    if (onlyRejected) arr = arr.filter(s => s.rejectionsCount > 0);
    const q = filter.trim().toLowerCase();
    if (q) {
      arr = arr.filter(s =>
        s.number.toLowerCase().includes(q) ||
        s.title.toLowerCase().includes(q) ||
        (s.intakeName ?? '').toLowerCase().includes(q) ||
        (s.clientName ?? '').toLowerCase().includes(q));
    }
    return arr;
  }, [studiesList, filter, onlyRejected]);

  const totalRejections = useMemo(
    () => (studiesList ?? []).reduce((sum, s) => sum + s.rejectionsCount, 0),
    [studiesList]);

  async function saveNotes() {
    if (!zz) return;
    setSavingNotes(true);
    try {
      const updated = await zarzadyZlewni.updateNotes(zz.id, notes);
      setZz(updated);
      setNotesDirty(false);
      setNotesSavedAt(Date.now());
    } catch (e) {
      setErr(String(e));
    } finally {
      setSavingNotes(false);
    }
  }

  async function saveCustomPrompt() {
    if (!zz) return;
    setSavingPrompt(true);
    try {
      const updated = await zarzadyZlewni.updateCustomAiPrompt(
        zz.id, customPrompt.trim() ? customPrompt : null);
      setZz(updated);
      setCustomPrompt(updated.customAiPrompt ?? '');
      setPromptDirty(false);
      setPromptSavedAt(Date.now());
    } catch (e) {
      setErr(String(e));
    } finally {
      setSavingPrompt(false);
    }
  }

  function fmtDecisionDays(days: number | null | undefined): string {
    if (days == null) return '—';
    if (days < 1) return Math.round(days * 24) + ' h';
    if (days < 30) return days.toFixed(1) + ' dni';
    return (days / 30).toFixed(1) + ' mies.';
  }

  if (err) return <div className="text-red-500 p-4">Błąd: {err}</div>;
  if (!zz) return <div className="text-ink-400 p-4">Ładowanie…</div>;

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-start justify-between mb-5 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-400 font-mono">{zz.code}</div>
          <h1>{zz.name}</h1>
          <div className="text-ink-500 text-[13px]">{zz.rzgwName}</div>
        </div>
        <NavLink to="/admin/zarzady-zlewni" className="btn btn-secondary btn-sm">
          ← Wszystkie ZZ
        </NavLink>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="card lg:col-span-1">
          <div className="card-head"><div className="card-title">Adres i kontakt</div></div>
          <div className="card-body space-y-3 text-[13px]">
            <Field label="Adres">
              {zz.addressLine1 ? (
                <>
                  <div>{zz.addressLine1}</div>
                  <div className="text-ink-600">{zz.addressPostalCode} {zz.addressCity}</div>
                </>
              ) : <span className="text-ink-400 italic">brak danych</span>}
            </Field>
            <Field label="Telefon">
              {zz.phone
                ? <a href={`tel:${zz.phone.replace(/\s/g, '')}`} className="font-mono text-water-700 hover:underline">{zz.phone}</a>
                : <span className="text-ink-400 italic">brak</span>}
            </Field>
            <Field label="Email">
              {zz.email
                ? <a href={`mailto:${zz.email}`} className="text-water-700 hover:underline break-all">{zz.email}</a>
                : <span className="text-ink-400 italic">brak</span>}
            </Field>
            <Field label="ePUAP">
              {zz.epuapAddress
                ? <span className="font-mono text-[12px] break-all">{zz.epuapAddress}</span>
                : <span className="text-ink-400 italic">brak</span>}
            </Field>
            <Field label="e-Doręczenia">
              {zz.edoreczeniaAddress
                ? <span className="font-mono text-[12px] break-all">{zz.edoreczeniaAddress}</span>
                : <span className="text-ink-400 italic">brak</span>}
            </Field>
            {zz.voivodeshipHints && (
              <Field label="Województwa (orient.)">
                <span className="text-ink-600 text-[12px]">{zz.voivodeshipHints}</span>
              </Field>
            )}
          </div>
        </div>

        <div className="card lg:col-span-1">
          <div className="card-head"><div className="card-title">Statystyki</div></div>
          <div className="card-body grid grid-cols-2 gap-3">
            <Stat label="Ujęcia" value={zz.intakesCount ?? 0} />
            <Stat label="Opracowania" value={zz.studiesCount ?? 0} />
            <Stat label="Odrzucenia" value={totalRejections} accent={totalRejections > 0 ? 'amber' : undefined} />
            <div className="border border-ink-100 rounded p-3 flex flex-col">
              <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-0.5">
                Śr. czas decyzji
              </div>
              <div className="font-mono tabular-nums text-[18px] font-semibold text-ink-900">
                {fmtDecisionDays(zz.avgDecisionDays)}
              </div>
              <div className="text-[10px] text-ink-400 mt-0.5">
                {zz.completedStudiesCount && zz.completedStudiesCount > 0
                  ? `z ${zz.completedStudiesCount} zakończonych`
                  : 'brak zakończonych opracowań'}
              </div>
            </div>
          </div>
        </div>

        <div className="card lg:col-span-1">
          <div className="card-head">
            <div className="card-title">Uwagi własne</div>
            <div className="ml-auto flex items-center gap-2">
              {notesSavedAt && !notesDirty && (
                <span className="text-[11px] text-emerald-600">Zapisano ✓</span>
              )}
              <button
                onClick={saveNotes}
                disabled={!notesDirty || savingNotes}
                className="btn btn-primary btn-sm"
              >
                {savingNotes ? 'Zapisywanie…' : 'Zapisz'}
              </button>
            </div>
          </div>
          <div className="card-body">
            <textarea
              value={notes}
              onChange={e => { setNotes(e.target.value); setNotesDirty(true); }}
              rows={8}
              placeholder="Notatki, uwagi, kontakty osobiste, specyfika ZZ…"
              className="input w-full text-[13px] font-mono"
            />
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Dodatkowy prompt AI dla tego ZZ</div>
          <span className="ml-2 text-[11px] text-ink-400">opcjonalnie</span>
          <div className="ml-auto flex items-center gap-2">
            {promptSavedAt && !promptDirty && (
              <span className="text-[11px] text-emerald-600">Zapisano ✓</span>
            )}
            <button
              onClick={saveCustomPrompt}
              disabled={!promptDirty || savingPrompt}
              className="btn btn-primary btn-sm"
            >
              {savingPrompt ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="text-[12px] text-ink-600 mb-2 p-2.5 bg-ai-500/5 border border-ai-500/20 rounded">
            <div className="font-semibold text-ai-700 mb-0.5">Jak to działa</div>
            Treść poniżej jest <strong>doklejana do każdego promptu generującego draft AI</strong> dla
            opracowań, których ujęcie podlega pod ten ZZ. Wykorzystaj to, żeby uwzględnić specyficzne
            wymagania urzędu — np. „w sekcji Strefy ochronne podawaj zawsze numery działek
            ewidencyjnych", „cytuj wyłącznie ustawę Prawo wodne (Dz.U. 2022 poz. 2625), nie obowiązującą
            wersję wstępną".
          </div>
          <textarea
            value={customPrompt}
            onChange={e => { setCustomPrompt(e.target.value); setPromptDirty(true); }}
            rows={6}
            placeholder='np. „W sekcji Warunki hydrogeologiczne wymagaj wprost odniesienia do MhP 1:50k arkusz nr X. Nie używaj słowa „prawdopodobnie" — ten ZZ zwraca operaty z taką niepewnością."'
            className="input w-full text-[13px] font-mono"
          />
          <div className="text-[10px] text-ink-400 mt-1">
            Pozostaw puste, żeby nie doklejać niczego.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Opracowania powiązane z ZZ ({studiesList?.length ?? 0})</div>
          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[12px] text-ink-600 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyRejected}
                onChange={e => setOnlyRejected(e.target.checked)}
              />
              Tylko z odrzuceniami
            </label>
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filtr (numer, tytuł, ujęcie, klient)…"
              className="input input-sm w-72"
            />
          </div>
        </div>
        <div className="card-body !p-0">
          {!studiesList && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {studiesList && filteredStudies.length === 0 && (
            <div className="p-4 text-ink-400 text-[12px]">
              {studiesList.length === 0
                ? 'Brak opracowań przypisanych do tego ZZ.'
                : 'Brak opracowań pasujących do filtra.'}
            </div>
          )}
          {studiesList && filteredStudies.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>Tytuł / Ujęcie / Klient</th>
                  <th>Typ</th>
                  <th>Status</th>
                  <th className="text-right">Odrzucenia</th>
                  <th>Utworzone</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudies.map(s => (
                  <tr key={s.id} className="cursor-pointer hover:bg-ink-50"
                      onClick={() => window.location.assign(`/studies/${s.id}`)}>
                    <td className="font-mono text-[12px] font-semibold">{s.number}</td>
                    <td>
                      <div className="font-semibold text-ink-900 text-[13px]">{s.title}</div>
                      <div className="text-[11px] text-ink-500">
                        {s.intakeName && <span>Ujęcie: {s.intakeName}</span>}
                        {s.intakeName && s.clientName && <span> · </span>}
                        {s.clientName && <span>Klient: {s.clientName}</span>}
                      </div>
                    </td>
                    <td className="text-[12px] font-mono">{s.typeAbbreviation}</td>
                    <td>
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                        style={{ backgroundColor: s.statusColor + '22', color: s.statusColor }}
                      >
                        {s.statusName}
                      </span>
                    </td>
                    <td className="text-right font-mono tabular-nums">
                      {s.rejectionsCount > 0
                        ? <span className="text-amber-700 font-semibold">{s.rejectionsCount}×</span>
                        : <span className="text-ink-400">0</span>}
                    </td>
                    <td className="text-[11px] text-ink-500 font-mono">
                      {s.createdAt ? new Date(s.createdAt).toLocaleDateString('pl-PL') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-0.5">{label}</div>
      <div className="text-ink-800">{children}</div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: 'amber' }) {
  return (
    <div className="text-center">
      <div className={
        'text-[24px] font-bold font-mono tabular-nums ' +
        (accent === 'amber' ? 'text-amber-700' : 'text-ink-900')
      }>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-ink-500">{label}</div>
    </div>
  );
}
